import React, { useState, useRef, useEffect } from "react";
import { api, API } from "../lib/api";
import { getVoice, getSpeed, setSpeed as persistSpeed, SPEEDS } from "../lib/settings";
import { primeAudio } from "../lib/audio";
import { Volume2, Pause, Play, RotateCcw, Loader2, Gauge } from "lucide-react";

// Read-aloud with true pause/resume + restart. Voice + default speed come from Settings.
export default function ReadAloud({ text, color = "#38BDF8", label = "Read aloud" }) {
  const [speed, setSpeed] = useState(getSpeed());
  const [state, setState] = useState("idle"); // idle | loading | playing | paused
  const audioRef = useRef(null);

  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const reset = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setState("idle");
  };

  const start = async () => {
    setState("loading");
    try {
      let audio = audioRef.current;
      if (!audio) { audio = new Audio(); audioRef.current = audio; }
      primeAudio(audio); // unlock within the click gesture (before the async fetch)
      const { url } = await api.tts(text, speed, getVoice());
      audio.onended = () => setState("idle");
      audio.onerror = () => setState("idle");
      audio.src = `${API.replace(/\/api$/, "")}${url}`;
      audio.currentTime = 0;
      await audio.play();
      setState("playing");
    } catch (e) {
      setState("idle");
    }
  };

  const onMain = async () => {
    if (state === "playing") { audioRef.current?.pause(); setState("paused"); return; }
    if (state === "paused" && audioRef.current) { await audioRef.current.play(); setState("playing"); return; }
    if (state === "idle") await start();
  };

  const restart = async () => {
    if (!audioRef.current) { await start(); return; }
    audioRef.current.currentTime = 0;
    await audioRef.current.play();
    setState("playing");
  };

  const cycleSpeed = () => {
    const i = SPEEDS.indexOf(speed);
    const nextSpeed = SPEEDS[(i + 1) % SPEEDS.length];
    setSpeed(nextSpeed);
    persistSpeed(nextSpeed); // remember as the default everywhere
    reset(); // new speed needs a fresh render of the audio
  };

  const mainIcon = state === "loading" ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
    : state === "playing" ? <Pause className="h-3.5 w-3.5" />
    : state === "paused" ? <Play className="h-3.5 w-3.5" />
    : <Volume2 className="h-3.5 w-3.5" />;
  const mainLabel = state === "playing" ? "Pause" : state === "paused" ? "Resume" : label;

  return (
    <div className="inline-flex items-center gap-1.5" data-testid="read-aloud">
      <button
        onClick={onMain}
        data-testid="read-aloud-play"
        className="press inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs"
        style={{ borderColor: `${color}66`, color }}
        title={mainLabel}
      >
        {mainIcon}
        {mainLabel}
      </button>
      {(state === "playing" || state === "paused") && (
        <button
          onClick={restart}
          data-testid="read-aloud-restart"
          className="press inline-flex items-center justify-center rounded-full border border-hair p-1.5 text-muted hover:text-ink"
          title="Restart from the beginning"
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      )}
      <button
        onClick={cycleSpeed}
        data-testid="read-aloud-speed"
        className="press inline-flex items-center gap-1 rounded-full border border-hair px-2 py-1 font-mono text-[10px] text-muted"
        title="Reading speed"
      >
        <Gauge className="h-3 w-3" /> {speed}x
      </button>
    </div>
  );
}
